import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMe } from '@/features/auth/useMe';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { ConsentChoices } from '@/features/consent-choices/ConsentChoices';

/** Optional post-join step. Membership already succeeded; no token is retained.
 * Server directory and subject eligibility own authority, never URL preferences.
 * Continuing does not grant or withdraw any permission. */
export function AssistChoicePage() {
  const me = useMe();
  const navigate = useNavigate();
  const search = useSearch({ from: '/assist-choice' });
  const redirected = useRef(false);
  useEffect(() => {
    const anonymous = me.isSuccess && !me.data?.user || me.isError && me.error instanceof ApiError && me.error.status === 401;
    if (!anonymous || redirected.current) return;
    redirected.current = true;
    const params = new URLSearchParams({ next: search.next });
    if (search.team) params.set('team', search.team);
    void navigate({ to: '/login', search: { next: `/assist-choice?${params}` }, replace: true });
  }, [me.isSuccess, me.isError, me.data, me.error, navigate, search.team, search.next]);
  if (me.isLoading) return <main className="p-6 text-ink-muted" role="status">Loading your account…</main>;
  if (me.isError && !(me.error instanceof ApiError && me.error.status === 401)) return <main className="mx-auto max-w-xl space-y-3 p-6 text-ink"><p role="alert">Could not load your account. Your team membership and consent choices have not been changed.</p><Button onClick={() => void me.refetch()}>Retry</Button><Link to={search.next} className="block text-accent underline">Continue</Link></main>;
  if (!me.data?.user) return null;
  return <main className="px-4 py-8 sm:py-12" data-testid="assist-choice-page"><div className="mx-auto w-full max-w-6xl space-y-5">
    <header className="space-y-2"><p className="text-xs font-display uppercase tracking-wider text-ink-muted">Your account</p><h1 className="font-display text-2xl text-ink">Consent choices</h1></header>
    <ConsentChoices />
    <div className="flex flex-col gap-3 border-t border-rule pt-4 sm:flex-row sm:items-center sm:justify-between"><p className="max-w-sm text-xs text-ink-muted">No choice is saved by continuing. You can change it later in Account → Consent.</p><Button onClick={() => void navigate({ to: search.next, replace: true })}>Continue to Tktic</Button></div>
    <nav className="flex gap-4 text-xs text-ink-muted" aria-label="Consent information"><Link to="/terms" className="underline">Terms</Link><Link to="/privacy" className="underline">Privacy</Link></nav>
  </div></main>;
}
